class Header {
  constructor() {
    this.header = document.querySelector('.header');
    if (!this.header) return;

    this.scrollThreshold = parseInt(this.header.dataset.scrollThreshold) || 300;
    this.transparentTextColor = this.header.dataset.transparentText || 'white';
    this.lastScrollY = window.scrollY;
    this.isTransparent = true;

    this.initAnnouncementSwiper();
    this.initScrollBehavior();
    this.initMobileMenu();
    this.initSearchModal();
    this.initMegamenu();
    this.initDropdowns();
    this.initLocalizationSelectors();
    this.updateCartCount();
  }

  initAnnouncementSwiper() {
    const announcementBar = document.querySelector('[data-announcement-bar]');
    if (!announcementBar) return;

    const swiperElement = announcementBar.querySelector('.swiper');
    if (!swiperElement) return;

    const slides = swiperElement.querySelectorAll('.swiper-slide');
    if (slides.length <= 1) return;

    // Wait for Swiper to be loaded
    if (typeof Swiper === 'undefined') {
      setTimeout(() => this.initAnnouncementSwiper(), 100);
      return;
    }

    // eslint-disable-next-line no-undef
    this.announcementSwiper = new Swiper(swiperElement, {
      loop: true,
      autoplay: {
        delay: 5000,
        disableOnInteraction: false,
      },
      speed: 600,
      navigation: {
        nextEl: '.header__announcement-next',
        prevEl: '.header__announcement-prev',
      },
    });
  }

  initScrollBehavior() {
    let ticking = false;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    });

    this.handleScroll();
  }

  handleScroll() {
    const currentScrollY = window.scrollY;

    if (currentScrollY < this.scrollThreshold) {
      this.header.classList.add('header--transparent');
      if (this.transparentTextColor === 'white') {
        this.header.classList.add('header--transparent-white');
      }
      this.header.classList.remove('header--hidden');
      this.isTransparent = true;
    } else {
      this.header.classList.remove('header--transparent', 'header--transparent-white');
      this.isTransparent = false;

      if (currentScrollY > this.lastScrollY) {
        this.header.classList.add('header--hidden');
      } else {
        this.header.classList.remove('header--hidden');
        this.header.classList.add('header--visible');
      }
    }

    this.lastScrollY = currentScrollY;
  }

  initMobileMenu() {
    const burgerToggle = this.header.querySelector('[data-burger-toggle]');
    const menuDrawer = document.querySelector('[data-menu-drawer]');
    const overlay = this.header.querySelector('[data-header-overlay]');

    if (!burgerToggle || !menuDrawer) return;

    let stickyAtcWasVisible = false;

    // Close drawer function
    const closeMenuDrawer = () => {
      menuDrawer.classList.remove('menu-drawer--open');
      burgerToggle.classList.remove('is-open');
      burgerToggle.setAttribute('aria-expanded', 'false');
      if (overlay) overlay.classList.remove('header__overlay--visible');
      document.body.style.overflow = '';
      this.closeAllSubmenus();

      const stickyAtc = document.querySelector('[data-sticky-atc]');
      if (stickyAtc && stickyAtcWasVisible) {
        stickyAtc.classList.remove('is-hidden-by-drawer');
      }
    };

    // Open drawer function
    const openMenuDrawer = () => {
      const headerRect = this.header.getBoundingClientRect();
      const drawerTop = headerRect.bottom;
      document.documentElement.style.setProperty('--drawer-top', `${drawerTop}px`);

      menuDrawer.classList.add('menu-drawer--open');
      burgerToggle.classList.add('is-open');
      burgerToggle.setAttribute('aria-expanded', 'true');
      if (overlay) overlay.classList.add('header__overlay--visible');
      document.body.style.overflow = 'hidden';

      const stickyAtc = document.querySelector('[data-sticky-atc]');
      stickyAtcWasVisible = stickyAtc && stickyAtc.classList.contains('is-visible');
      if (stickyAtcWasVisible) {
        stickyAtc.classList.add('is-hidden-by-drawer');
      }
    };

    // Toggle drawer on burger click (burger becomes X when open)
    burgerToggle.addEventListener('click', () => {
      const isOpen = menuDrawer.classList.contains('menu-drawer--open');
      if (isOpen) {
        closeMenuDrawer();
      } else {
        openMenuDrawer();
      }
    });

    if (overlay) {
      overlay.addEventListener('click', closeMenuDrawer);
    }

    // Escape key closes drawer
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menuDrawer.classList.contains('menu-drawer--open')) {
        closeMenuDrawer();
      }
    });

    // Initialize accordions
    this.initDrawerAccordions();

    // Initialize submenus
    this.initDrawerSubmenus();
  }

  initDrawerAccordions() {
    const accordions = document.querySelectorAll('[data-accordion]');

    accordions.forEach((accordion) => {
      const header = accordion.querySelector('.menu-drawer__accordion-header');
      if (!header) return;

      header.addEventListener('click', (e) => {
        e.preventDefault();
        const isOpen = accordion.hasAttribute('open');

        // Close other accordions (only one open at a time)
        accordions.forEach((other) => {
          if (other !== accordion) {
            other.removeAttribute('open');
          }
        });

        // Toggle current accordion
        if (isOpen) {
          accordion.removeAttribute('open');
        } else {
          accordion.setAttribute('open', '');
        }
      });
    });
  }

  initDrawerSubmenus() {
    const submenuTriggers = document.querySelectorAll('[data-submenu-trigger]');
    const backButtons = document.querySelectorAll('[data-submenu-back]');
    const navLevel1 = document.querySelector('[data-drawer-nav-level="1"]');

    submenuTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const submenuId = trigger.dataset.submenuTrigger;
        const submenu = document.querySelector(`[data-submenu="${submenuId}"]`);

        if (submenu) {
          // Hide level 1 nav
          if (navLevel1) navLevel1.classList.add('is-hidden');
          // Show submenu (slide from right)
          submenu.classList.add('menu-drawer__submenu--open');
        }
      });
    });

    backButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const submenu = button.closest('[data-submenu]');
        if (submenu) {
          submenu.classList.remove('menu-drawer__submenu--open');
          // Show level 1 nav
          if (navLevel1) navLevel1.classList.remove('is-hidden');
        }
      });
    });
  }

  closeAllSubmenus() {
    const submenus = document.querySelectorAll('[data-submenu]');
    const navLevel1 = document.querySelector('[data-drawer-nav-level="1"]');
    const accordions = document.querySelectorAll('[data-accordion]');

    submenus.forEach((submenu) => {
      submenu.classList.remove('menu-drawer__submenu--open');
    });

    if (navLevel1) navLevel1.classList.remove('is-hidden');

    // Close all accordions
    accordions.forEach((accordion) => {
      accordion.removeAttribute('open');
    });
  }

  initSearchModal() {
    const searchToggle = this.header.querySelector('[data-search-toggle]');
    const searchModal = this.header.querySelector('[data-search-modal]');
    const searchClose = this.header.querySelector('[data-search-close]');

    if (!searchToggle || !searchModal) return;

    searchToggle.addEventListener('click', () => {
      searchModal.classList.add('header__search-modal--open');
      document.body.style.overflow = 'hidden';

      setTimeout(() => {
        const searchInput = searchModal.querySelector('.header__search-input');
        if (searchInput) searchInput.focus();
      }, 100);
    });

    const closeSearchModal = () => {
      searchModal.classList.remove('header__search-modal--open');
      document.body.style.overflow = '';
    };

    if (searchClose) {
      searchClose.addEventListener('click', closeSearchModal);
    }

    searchModal.addEventListener('click', (e) => {
      if (e.target === searchModal) {
        closeSearchModal();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && searchModal.classList.contains('header__search-modal--open')) {
        closeSearchModal();
      }
    });
  }

  initMegamenu() {
    const triggers = this.header.querySelectorAll('[data-megamenu-trigger]');

    if (!triggers.length) return;

    this.updateMegamenuPosition();
    window.addEventListener('resize', () => this.updateMegamenuPosition());
    window.addEventListener('scroll', () => this.updateMegamenuPosition());

    let timeout;

    const closeAll = () => {
      document.querySelectorAll('[data-megamenu]').forEach((m) => {
        m.classList.remove('header__megamenu--open');
      });
      triggers.forEach((t) => t.classList.remove('is-active'));
    };

    triggers.forEach((trigger) => {
      const id = trigger.dataset.megamenuTrigger;
      const megamenu = document.querySelector(`[data-megamenu="${id}"]`);

      if (!megamenu) return;

      const open = () => {
        clearTimeout(timeout);
        closeAll();
        megamenu.classList.add('header__megamenu--open');
        trigger.classList.add('is-active');
      };

      const close = () => {
        timeout = setTimeout(() => {
          megamenu.classList.remove('header__megamenu--open');
          trigger.classList.remove('is-active');
        }, 200);
      };

      trigger.addEventListener('mouseenter', open);
      trigger.addEventListener('mouseleave', close);

      megamenu.addEventListener('mouseenter', () => {
        clearTimeout(timeout);
      });

      megamenu.addEventListener('mouseleave', close);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeAll();
      }
    });
  }

  initDropdowns() {
    const triggers = this.header.querySelectorAll('[data-dropdown-trigger]');

    triggers.forEach((trigger) => {
      const id = trigger.dataset.dropdownTrigger;
      const dropdown = document.querySelector(`[data-dropdown="${id}"]`);
      if (!dropdown) return;

      let timeout;

      const updatePosition = () => {
        const rect = trigger.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const bottomY = rect.bottom + 30 ;
        dropdown.style.setProperty('--dropdown-top', `${bottomY}px `) ;
        dropdown.style.setProperty('--dropdown-left', `${centerX}px`);
      };

      const open = () => {
        clearTimeout(timeout);
        updatePosition();
        dropdown.classList.add('header__dropdown--open');
        trigger.classList.add('is-active');
      };

      const close = () => {
        timeout = setTimeout(() => {
          dropdown.classList.remove('header__dropdown--open');
          trigger.classList.remove('is-active');
        }, 200);
      };

      trigger.addEventListener('mouseenter', open);
      trigger.addEventListener('mouseleave', close);

      dropdown.addEventListener('mouseenter', () => {
        clearTimeout(timeout);
      });

      dropdown.addEventListener('mouseleave', close);
    });
  }

  initLocalizationSelectors() {
    // Fix Shopify auto-translate: set labels from data attributes + active state from URL
    const path = window.location.pathname;
    const localeMatch = path.match(/^\/(en|de|it|es|pt|nl|ja)(\/|$)/);
    const currentLocale = localeMatch ? localeMatch[1] : 'fr';

    document.querySelectorAll('[data-lang-label]').forEach((el) => {
      el.textContent = el.dataset.langLabel;
      if (el.getAttribute('hreflang') === currentLocale) {
        el.classList.add('header__selector-option--active');
      } else {
        el.classList.remove('header__selector-option--active');
      }
    });

    const langCurrentBtn = document.querySelector('[data-lang-current]');
    if (langCurrentBtn) langCurrentBtn.textContent = currentLocale.toUpperCase();

    const wrappers = document.querySelectorAll('[data-selector-wrapper]');

    wrappers.forEach((wrapper) => {
      const toggle = wrapper.querySelector('[data-selector-toggle]');
      const type = toggle ? toggle.dataset.selectorToggle : null;
      const dropdown = wrapper.querySelector('[data-selector-dropdown]');

      if (!toggle || !dropdown) return;

      // Toggle dropdown on button click
      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = dropdown.classList.contains('header__selector-dropdown--open');

        this.closeAllDropdowns();

        if (!isOpen) {
          dropdown.classList.add('header__selector-dropdown--open');
          toggle.setAttribute('aria-expanded', 'true');
        }
      });

      // Les options du selecteur de pays sont des boutons submit natifs
      // (name="country_code"), aucun JS n'est necessaire pour changer de marche.
    });

    // Close dropdowns on outside click
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-selector-wrapper]')) {
        this.closeAllDropdowns();
      }
    });

    // Close dropdowns on Escape key, and give focus back to the toggle
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;

      const openDropdown = document.querySelector('.header__selector-dropdown--open');
      const openToggle = openDropdown
        ? openDropdown.closest('[data-selector-wrapper]').querySelector('[data-selector-toggle]')
        : null;

      this.closeAllDropdowns();

      if (openToggle) openToggle.focus();
    });
  }

  closeAllDropdowns() {
    const dropdowns = document.querySelectorAll('.header__selector-dropdown--open');
    const toggles = document.querySelectorAll('[data-selector-toggle]');

    dropdowns.forEach((dropdown) => {
      dropdown.classList.remove('header__selector-dropdown--open');
    });

    toggles.forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'false');
    });
  }

  updateMegamenuPosition() {
    const headerPill = this.header.querySelector('.header__pill--left');
    if (!headerPill) return;

    const rect = headerPill.getBoundingClientRect();
    const megamenuTop = rect.bottom + 8; // 8px below header
    document.documentElement.style.setProperty('--megamenu-top', `${megamenuTop}px`);
  }

  updateCartCount() {
    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => {
        const cartCount = this.header.querySelector('[data-cart-count]');
        if (cartCount) {
          cartCount.textContent = cart.item_count;
          cartCount.style.display = cart.item_count > 0 ? 'flex' : 'none';
        }
      })
      .catch((error) => console.error('Error fetching cart:', error));
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new Header());
} else {
  new Header();
}

document.addEventListener('cart:update', () => {
  const header = new Header();
  header.updateCartCount();
});
